import { indigo, white } from '@kmns-internal/furikake'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useDownloadSampleCsv } from '../downloadSampleCsv'
import { GlossaryJob } from '../types'

const formatDate = (value: Date) =>
  value.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

type Props = {
  data: GlossaryJob[]
  isLoading: boolean
  onDownloadCsv: (id: number, fileName: string) => void
  isDownloading?: boolean
  activeJobId?: number | null
}

export const GlossaryTable = ({
  data,
  isLoading,
  onDownloadCsv,
  isDownloading = false,
  activeJobId,
}: Props) => {
  const downloadSampleCsv = useDownloadSampleCsv()

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
          bgcolor: white,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <TableContainer
      sx={{
        overflow: 'auto',
        position: 'relative',
        flexGrow: 1,
        bgcolor: white,
      }}
    >
      <Table sx={{ bgcolor: white, width: '100%' }} stickyHeader>
        <TableHead sx={{ backgroundColor: white }}>
          <TableRow>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
                width: '22%',
              }}
            >
              登録日時
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
                width: '35%',
              }}
            >
              ファイル名
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
                width: '20%',
              }}
            >
              実行者
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
                width: 'auto',
              }}
              align="right"
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                <Typography variant="body2" color="text.secondary">
                  データが登録されていません。
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={downloadSampleCsv}
                  >
                    専用フォーマット（CSV）をダウンロード
                  </Link>
                  後、内容を記入してからアップロードしてください。
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map(record => (
              <TableRow key={record.id} hover>
                <TableCell
                  sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  {formatDate(record.createdAt)}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    {record.fileName}
                    {activeJobId === record.id && (
                      <Chip
                        label="適用中"
                        size="small"
                        sx={{
                          bgcolor: indigo[1],
                          color: 'text.primary',
                          border: 'none',
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  {record.uploadedBy}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                  align="right"
                >
                  <Button
                    startIcon={<DownloadIcon />}
                    size="small"
                    onClick={() => onDownloadCsv(record.id, record.fileName)}
                    disabled={isDownloading}
                    sx={{ textTransform: 'none' }}
                  >
                    ダウンロード
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
